import { PRODUCT_NAME, onColorFor } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, cn, cssVars, initialsOf, type IconName } from "@sagot/ui";
import { effectiveAccent, previewAvatar, previewStarters, type WebappSettings, type WebappStudioOut } from "./webapp-model";

export type PreviewDevice = "desktop" | "mobile";
export type PreviewTheme = "light" | "dark";

const FILL = "bg-[var(--wg-primary)] text-[var(--wg-on-primary)]";
const BOX = { xs: "size-6 rounded-sm text-[9px]", sm: "size-8 rounded-md text-[11px]", md: "size-14 rounded-[16px] text-base" } as const;
const GLYPH = { xs: "size-3.5", sm: "size-4", md: "size-7" } as const;
const CARD_ICONS: readonly IconName[] = ["message-circle", "wallet", "list-checks", "calendar"];

interface PreviewProps {
  settings: WebappSettings;
  design: WebappStudioOut;
  agentName: string;
  device: PreviewDevice;
  theme: PreviewTheme;
}

const pick = (value: { en: string; fil: string }, language: string): string => (language === "fil" ? value.fil || value.en : value.en);

function Mark({ settings, design, name, size }: { settings: WebappSettings; design: WebappStudioOut; name: string; size: keyof typeof BOX }) {
  const icon = settings.icons.assistant_icon;
  if (icon !== "avatar") {
    return (
      <span aria-hidden="true" data-testid="preview-icon" className={cn("inline-flex shrink-0 items-center justify-center", FILL, BOX[size])}>
        <Icon name={icon as IconName} className={GLYPH[size]} />
      </span>
    );
  }
  const picture = previewAvatar(settings, design.widget.avatar_url ?? null);
  if (picture) return <img src={picture} alt="" aria-hidden="true" data-testid="preview-avatar" className={cn("shrink-0 border border-line bg-surface object-cover", BOX[size])} />;
  const logo = settings.branding.avatar_source === "initials" ? null : (settings.branding.logo_url ?? design.workspace_logo_url ?? null);
  if (logo) return <img src={logo} alt="" aria-hidden="true" data-testid="preview-logo" className={cn("shrink-0 border border-line bg-surface object-contain p-0.5", BOX[size])} />;
  const letters = settings.branding.avatar_source === "initials" ? initialsOf(name) : initialsOf(design.workspace_name);
  return (
    <span aria-hidden="true" data-testid="preview-initials" className={cn("inline-flex shrink-0 items-center justify-center font-extrabold tracking-tight", FILL, BOX[size])}>
      {letters}
    </span>
  );
}

function Sidebar({ settings, design, appName }: { settings: WebappSettings; design: WebappStudioOut; appName: string }) {
  const { t } = useTranslation();
  const logo = settings.branding.logo_url ?? design.workspace_logo_url ?? settings.branding.avatar_url ?? null;
  return (
    <aside aria-hidden="true" className="hidden w-52 shrink-0 flex-col border-r border-line bg-surface md:flex">
      <div className="flex h-12 items-center gap-2 border-b border-line px-3">
        {logo ? <img src={logo} alt="" className="size-7 rounded-md border border-line object-contain" /> : <span className={cn("inline-flex size-7 items-center justify-center rounded-md text-[10px] font-extrabold", FILL)}>{initialsOf(design.workspace_name)}</span>}
        <span className="min-w-0 truncate text-xs font-extrabold" data-testid="preview-app-name">{appName}</span>
      </div>
      <div className="space-y-2 p-3">
        <span className={cn("flex h-8 items-center justify-center gap-1 rounded-md text-xs font-semibold", FILL)}>
          <Icon name="plus" className="size-3.5" />
          {t("webapp.sidebar.newChat")}
        </span>
        {settings.layout.show_history ? (
          <div data-testid="preview-history" className="space-y-1.5 pt-1">
            <span className="block h-2.5 w-3/4 rounded-xs bg-sunken" />
            <span className="block h-2.5 w-2/3 rounded-xs bg-sunken" />
            <span className="block h-2.5 w-4/5 rounded-xs bg-sunken" />
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export function WebappPreview({ settings, design, agentName, device, theme }: PreviewProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const accent = effectiveAccent(settings, design.widget.primary_color);
  const radius = settings.branding.radius_px ?? design.widget.radius_px;
  const title = pick(settings.content.title, language) || t("webapp.welcome.greetingGuest");
  const subtitle = pick(settings.content.subtitle, language) || pick(settings.layout.guest_notice, language) || t("webapp.welcome.subGuest");
  const placeholder = pick(settings.content.placeholder, language) || t("webapp.composer.placeholder");
  const disclaimer = pick(settings.content.disclaimer, language) || t("webapp.composer.disclaimer", { org: design.workspace_name });
  const cards = previewStarters(settings, design.widget.starter_questions).slice(0, 4);
  const appName = settings.branding.app_name.trim() || `${design.workspace_name} Ask`;
  const poweredBy = settings.layout.powered_by === "show" || (settings.layout.powered_by === "widget" && design.widget.show_branding) || !design.can_remove_branding;
  const compact = settings.branding.density === "compact";
  const mobile = device === "mobile";
  return (
    <div
      data-theme={theme}
      data-testid="webapp-preview"
      style={cssVars({ "--wg-primary": accent, "--wg-on-primary": onColorFor(accent), "--wg-radius": `${String(radius)}px` })}
      className={cn("mx-auto flex overflow-hidden rounded-xl border border-line bg-canvas text-fg shadow-card", mobile ? "h-[640px] w-[375px] max-w-full" : "h-[620px] w-full max-w-5xl")}
    >
      {mobile ? null : <Sidebar settings={settings} design={design} appName={appName} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
          <Mark settings={settings} design={design} name={agentName} size="xs" />
          <span className="min-w-0 truncate text-sm font-bold" data-testid="preview-agent">{agentName || t("webappStudio.console.preview.assistantName")}</span>
          <Icon name="chevron-down" className="size-4 shrink-0 text-fg-subtle" />
          <div className="ml-auto flex items-center gap-1 text-xs font-semibold text-fg-muted">
            {settings.share.enabled ? (
              <span data-testid="preview-share" className="inline-flex h-8 items-center gap-1 rounded-md px-2">
                <Icon name="link" className="size-3.5" />
                {mobile ? null : t("webapp.header.share")}
              </span>
            ) : null}
            <span className="inline-flex size-8 items-center justify-center rounded-md"><Icon name="pencil" className="size-3.5" /></span>
          </div>
        </div>
        <div className={cn("flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-4 text-center", compact ? "py-3" : "py-6")}>
          <Mark settings={settings} design={design} name={agentName} size="md" />
          <h2 className="mt-3 text-xl font-extrabold tracking-tight" data-testid="preview-title">{title}</h2>
          <p className="mt-1 max-w-sm text-sm text-fg-muted" data-testid="preview-subtitle">{subtitle}</p>
          {cards.length > 0 ? (
            <ul data-testid="preview-cards" className={cn("mt-4 grid w-full max-w-xl gap-2 text-left", mobile ? "grid-cols-1" : "grid-cols-2")}>
              {cards.map((question, index) => {
                const cardIcon = settings.icons.card_icon;
                const glyph = cardIcon === "auto" ? CARD_ICONS[index % CARD_ICONS.length] : cardIcon === "none" ? null : (cardIcon as IconName);
                return (
                  <li key={`${String(index)}-${question}`} className={cn("flex items-center gap-2.5 rounded-lg border border-line bg-surface", compact ? "p-2" : "p-3")}>
                    {glyph ? (
                      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-sunken text-primary-ink"><Icon name={glyph} className="size-3.5" /></span>
                    ) : null}
                    <span className="min-w-0 truncate text-xs font-bold">{question}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <div className="shrink-0 px-3 pb-3">
          <div className="rounded-xl border border-line-strong bg-surface p-2">
            <div className="px-2 py-1.5 text-sm text-fg-subtle" data-testid="preview-placeholder">{placeholder}</div>
            <div className="flex items-center justify-end">
              <span data-testid="preview-send" className={cn("inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold", FILL, settings.icons.send_style === "icon" && "w-8 px-0")}>
                <Icon name="arrow-up" className="size-3.5" />
                {settings.icons.send_style === "label" ? t("webapp.composer.send") : null}
              </span>
            </div>
          </div>
          <p className="mt-2 text-center text-[11px] leading-snug text-fg-subtle" data-testid="preview-disclaimer">
            {disclaimer}
            {poweredBy ? <span data-testid="preview-powered-by"> · {t("webapp.login.poweredBy", { product: PRODUCT_NAME })}</span> : null}
          </p>
        </div>
      </div>
    </div>
  );
}
